import axios from '../http-common';
import { Logger } from '../../../../../share/utils/logger';
import { useAppSelector } from '../../../redux/hooks';
import { lddGlobalSessionInfo } from '../../navigationTypes';
import { RetainerState, ConsentFormState } from '../types';


class PortalDataService {
    private SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);

    private urlSuffix = '';

    constructor(state) {
        this.urlSuffix = `?SessionID=${this.SESSION_INFO.sid}&AppID=${state.appId}`;
    }

    getSessionId(){
        return this.SESSION_INFO.sid;
    }

    getSessionAppCode(){
        return this.SESSION_INFO.appCode;
    }

    getProvince(){
        return this.SESSION_INFO.appProvinceShort;
    }

    getFullProvince() {
        return this.SESSION_INFO.appProvince;
    }

    getPortalById(id): Promise<any[]> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/portal/${id}` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    getPortalByAppId(appId): Promise<any[]> {

        const url = `/${this.SESSION_INFO.appProvinceShort}/portal?SessionID=${this.SESSION_INFO.sid}&AppID=${appId}`;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.error('Error in getPortalByAppId: ', error);
                    reject();
                });
        });
    }

    getPortalByLawyerId(lawyerId, province: string, startDate?: string, endDate?: string): Promise<any[]> {
        let url = `/${this.SESSION_INFO.appProvinceShort}/shell/${lawyerId}?SessionID=${this.SESSION_INFO.sid}`;
        if (startDate && endDate) {
            url += `&startDate=${startDate}&endDate=${endDate}`;
        }
        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    Logger.error('Error in getPortalByLawyerId: ', error);
                    reject();
                });
        });
    }
    createPortal(language: string, extraContent: { includeEmail: boolean, replyToFirmContact: number, spouseEmailObject: { SpouseName: string | null, SpouseEmail: string | null }[] }, requestIDNumber?: string): Promise<number> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/createPortal/${language}` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url, {
                params: { 
                    includeEmail: extraContent.includeEmail, 
                    replyToFirmContact: extraContent.replyToFirmContact,
                    spouseEmailObject: JSON.stringify(extraContent.spouseEmailObject),
                    requestIDNumber: requestIDNumber
                }
            })
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }
    
                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    fetchEmailLogs(appId): Promise<any> {
        const url = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${this.SESSION_INFO.appProvinceShort}/${this.SESSION_INFO.firm.id}/commCenter/fetchEmailLogs` + this.urlSuffix;
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    appId: appId,
                    province: this.SESSION_INFO.appProvinceShort
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.message === 'error') {
                    throw new Error(data.error);
                }
                resolve(data);
            })
            .catch(error => {
                console.log('err', error);
                reject();
            });
        });
    }
    
    sendEmail(appId, emailData, isNotaryFirm: boolean, language: string) {
        const url = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${this.SESSION_INFO.appProvinceShort}/${this.SESSION_INFO.firm.id}/commCenter/sendEmail` + this.urlSuffix;
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    appId: appId,
                    province: this.SESSION_INFO.appProvinceShort,
                    isNotaryFirm: isNotaryFirm,
                    language: language,
                    body: emailData
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.message === 'error') {
                    throw new Error(data.error);
                }
                resolve(data);
            })
            .catch(error => {
                console.log('err', error);
                reject();
            });
        });
    }
    
    logEmail(appId, logData) {
        const url = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${this.SESSION_INFO.appProvinceShort}/${this.SESSION_INFO.firm.id}/commCenter/logEmail` + this.urlSuffix;
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    appId: appId,
                    province: this.SESSION_INFO.appProvinceShort,
                    body: logData
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.message === 'error') {
                    throw new Error(data.error);
                }
                resolve(data);
            })
            .catch(error => {
                console.log('err', error);
                reject();
            });
        });
    }

    setSessionDateTime(portalId, date) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/portal/session/setDateTime` + this.urlSuffix;
        date.startDateTime = new Date(date.startDateTime).toISOString();
        date.endDateTime = new Date(date.endDateTime).toISOString();
        const data = {portalId, date};

        return new Promise((resolve, reject) => {
            axios.post(url, data)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    getFileInfo(): Promise<any> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/getInfo` + this.urlSuffix;
        
        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }
                    resolve(response.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }
    getFileByAppId(appId: string | number): Promise<any> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/shell/file/${appId}?SessionID=${this.SESSION_INFO.sid}`;
        
        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') throw new Error(response.data.error);
                    resolve(response.data.data && response.data.data.length > 0 ? response.data.data[0] : null);
                })
                .catch((error) => {
                    Logger.error('Error fetching file in getFileByAppId: ', error);
                    resolve(null);
                });
        });
    }
    deleteChanges(): Promise<any> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/deleteChanges` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    async joinRemoteSigningPortal(portalId: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/joinRemoteSigningPortal/${portalId}` + this.urlSuffix;
            const res = await axios({ method: 'get', url });

            return res.data;
        } catch (error) {
            console.error('Error joining remote signing portal:', error);
            throw error; 
        }
    }

    async getTwillioToken() {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getTwillioToken` + this.urlSuffix;
            const res = await axios({ method: 'get', url });

            return res.data;
        } catch (error) {
            console.error('Error getting Twillio token:', error);
            throw error; 
        }
    }

    async getLawyerRemoteSigningLobbyData(appId: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getXMLFileData/${appId}/lawyerRemoteSigningLobby` + this.urlSuffix;

            const res = await axios({ method: 'get', url });

            return res.data;
        } catch (error) {
            console.error('Error getting lawyer remote signing lobby data:', error);
            throw error; 
        }
    }

    async getClientPortalDocuments(portalId: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocuments/${portalId}` + this.urlSuffix;
            const res = await axios({ method: 'get', url });
            return res.data;
        } catch (error) {
            console.error('Error getting client portal documents:', error);
            throw error; 
        }
    }

    // async getRemoteSigningDocuments(portalId: string) {
    //     const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocuments/${portalId}` + this.urlSuffix;
    //     const res = await axios({ method: 'get', url });
    //     return res.data;
    // }

    async getClientPortalDocumentFromLaywer(portalId, fileName: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocumentFromLaywer/${portalId}/${fileName}` + this.urlSuffix;

            const res = await axios({ method: 'get', url, responseType: 'blob'});
            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error(`Error downloading document from lawyer: ${fileName}`, error);
            throw error; 
        }
    }

    async getClientPortalDocumentFromClient(portalId, fileName: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocumentFromClient/${portalId}/${fileName}` + this.urlSuffix;

            const res = await axios({ method: 'get', url, responseType: 'blob'});
            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error(`Error downloading document from client: ${fileName}`, error);
            throw error; 
        }
    }

    async deleteClientPortalDocumentFromLawyer(portalId, fileName: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/deleteClientPortalDocumentFromLawyer/${portalId}/${fileName}` + this.urlSuffix;
            const resp = await axios({method: 'delete', url});
            return resp;
        } catch (error) {
            console.error(`Error deleting document from lawyer: ${fileName}`, error);
            throw error; 
        }
    }

    async getRemoteSigningDocumentUrl(portalId, fileName: string) {
        try {
            //const url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;

            let url = ``;
            if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
                url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;
            } else {
                url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;
            }

            return url;
        } catch (error) {
            console.error('Error generating remote signing document URL:', error);
            throw error; 
        }
    }

    async setRetainerType(retainerState: RetainerState, appId, portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/${appId}/${portalId}/setRetainerType` + this.urlSuffix;

            let config = {
                method: 'post',
                url: url,
                data: retainerState
            };

            const res = await axios.request(config);
            return res.data;
        } catch (error) {
            console.error('Error setting retainer type:', error);
            throw error; 
        }
    }

    async setConsentFormType(consentFormState: ConsentFormState, appId, portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/${appId}/${portalId}/setConsentFormType` + this.urlSuffix;
            
            const data = {
                DefaultConsentRequired: consentFormState.defaultConsentFormRequired,
                TextBoxConsentRequired: consentFormState.textBoxConsentFormRequired,
                UploadConsent: consentFormState.uploadConsentForm,
                TextBoxConsentContent: consentFormState.textBasedConsentForm
            };

            let config = {
                method: 'post',
                url: url,
                data: data
            };

            const res = await axios.request(config);
            return res.data;
        } catch (error) {
            console.error('Error setting consent form type:', error);
            throw error; 
        }
    }

    async getClientPortalRetainerInformation(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalRetainerInformation/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);
            return res.data;
        } catch (error) {
            console.error('Error getting client portal retainer information:', error);
            throw error; 
        }
    }

    async getClientPortalConsentFormInformation(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalConsentFormInformation/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);
            return res.data;
        } catch (error) {
            console.error('Error getting client portal consent form information:', error);
            throw error; 
        }
    }

    async getCurrentRetainer(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getCurrentRetainer/${portalId}` + this.urlSuffix;
            const fileName = 'retainer.pdf';

            const res = await axios({ method: 'get', url, responseType: 'blob'});
            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error downloading current retainer:', error);
            throw error; 
        }
    }

    async uploadDocument(uploadedFile, portalId, appId, fileName, folder) {

        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/${portalId}/uploadDocument/${folder}/${fileName}` + this.urlSuffix;

            let config = {
                method: 'post',
                maxBodyLength: Infinity,
                url: url,
                headers: { 'Content-Type': 'application/octet-stream' }, // ????
                data : uploadedFile
            };

            const res = await axios.request(config);
            return res.data;

        } catch (error) {
            console.warn(`Document upload failed: ${folder}/${fileName}`, error.message);
            throw new Error(`Document upload failed: ${folder}/${fileName} ${error.message}`);
        }

    }

    async getPropertyAttributesState(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getPropertyAttributesState/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting property attributes state:', error);
            throw error; 
        }
    }

    async getDocumentsFromClient(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getDocumentsFromClient/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting documents from client:', error);
            throw error; 
        }
    }

    async getRemoteSigningDocuments(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocuments/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting remote signing documents:', error);
            throw error; 
        }
    }

    async getRemoteSigningDocument(portalId, fileName) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocument/${portalId}/${fileName}` + this.urlSuffix;

            const res = await axios({ method: 'get', url, responseType: 'blob'});
            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error(`Error downloading remote signing document: ${fileName}`, error);
            throw error; 
        }
    }

    async deleteRemoteSigningDocumentFromLawyer(portalId, fileName: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/deleteRemoteSigningDocumentFromLawyer/${portalId}/${fileName}` + this.urlSuffix;
            const resp = await axios({method: 'delete', url});
            return resp;
        } catch (error) {
            console.error(`Error deleting remote signing document from lawyer: ${fileName}`, error);
            throw error; 
        }
    }

    async getDefaultRemoteSigningDocument(portalId, fileName) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getDefaultRemoteSigningDocument/${portalId}/${fileName}` + this.urlSuffix;

            const res = await axios({ method: 'get', url, responseType: 'blob'});
            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error(`Error downloading default remote signing document: ${fileName}`, error);
            throw error; 
        }
    }

    async getRemoteSigningRequestFromClient(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningRequestFromClient/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting remote signing request from client:', error);
            throw error; 
        }
    }

    async getClientInformation(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientInformation/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting client information:', error);
            throw error; 
        }
    }

    async getSourceOfFunds(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getSourceOfFunds/${portalId}` + this.urlSuffix;

            let config = {
                method: 'get',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting source of funds:', error);
            throw error; 
        }
    }

    async setClientPortalCustomFields(portalId, customFields, afterOrBeforeInit: string) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/setClientPortalCustomFields/${portalId}/${afterOrBeforeInit}` + this.urlSuffix;

            let config = {
                method: 'POST',
                url: url,
                data: customFields
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error setting client portal custom fields:', error);
            throw error; 
        }
    }

    async getClientPortalCustomFields(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalCustomFields/${portalId}` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting client portal custom fields:', error);
            throw error; 
        }
    }

    async getMortgageInfo(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getMortgageInfo/${portalId}` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting mortgage info:', error);
            throw error; 
        }
    }

    async ableToCreatePortal() {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/files/ableToCreatePortal/` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error checking if able to create portal:', error);
            throw error; 
        }
    }

    async getDefaultRemoteSigningFiles(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getDefaultRemoteSigningFiles/${portalId}` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting default remote signing files:', error);
            throw error; 
        }
    }

    async addDefaultDocumentToRemoteSigning(portalId, document) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/addDefaultDocumentToRemoteSigning/${portalId}` + this.urlSuffix;

            let config = {
                method: 'PUT',
                url: url,
                data: document
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error adding default document to remote signing:', error);
            throw error; 
        }
    }

    async updateClientNames(portalId, clientNames) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/updateClientNames/${portalId}` + this.urlSuffix;

            let config = {
                method: 'POST',
                url: url,
                data: clientNames
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error updating client names:', error);
            throw error; 
        }
    }

    async updateClientList(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/updateClientList/${portalId}` + this.urlSuffix;

            let config = {
                method: 'POST',
                url: url
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error updating client list:', error);
            throw error; 
        }
    }

    async getDocumentRequest(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getDocumentRequests/${portalId}` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting document request:', error);
            throw error; 
        }
    }

    async resendInvitation(portalId, includeFirmEmail: boolean) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/resendInvitation/${portalId}/${includeFirmEmail}` + this.urlSuffix;

            const config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error resending invitation:', error);
            throw error; 
        }
    }

    async sendIDVReminder(
        portalId,
        payload: {
            clients: { name: string; email: string }[];
            noteText: string;
        }
    ) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/sendIDVReminder/${portalId}` + this.urlSuffix;

            const config = {
                method: 'POST',
                url: url,
                data: payload,
            };

            const res = await axios.request(config);
            return res.data;
        } catch (error) {
            console.error("Error sending IDV reminder:", error);
            throw error;
        }
    }

    async getShortCode(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getShortCode/${portalId}` + this.urlSuffix;

            const config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting short code:', error);
            throw error; 
        }
    }

    async getSelectedCustomFieldSetsFromXML(portalId) {
        try {
            const url = `/${this.SESSION_INFO.appProvinceShort}/getSelectedCustomFieldSet/${portalId}` + this.urlSuffix;

            let config = {
                method: 'GET',
                url: url,
            };

            const res = await axios.request(config);

            return res.data;
        } catch (error) {
            console.error('Error getting selected custom field sets from XML:', error);
            throw error; 
        }
    }

    async checkFileByAppID(appId: string, existingAppId: string): Promise<boolean> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/checkFileByAppID/${appId}?SessionID=${this.SESSION_INFO.sid}&AppID=${existingAppId}`;
        
        try {
            const res = await axios.get(url);
            return res.data.exists;
        } catch (error) {
            console.log('error', error);
            return false;
        }
    }

    async deletePortal(portalId: number): Promise<void> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/portal/${portalId}` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.delete(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }
                    resolve();
                })
                .catch((error) => {
                    console.log('error deleting portal', error);
                    reject(error);
                });
        });
    }

}


export default PortalDataService;
